import React, { useEffect, useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import {
  FieldArrayProvider,
  useFieldArrayMethods,
  useFieldArrayState,
  useFormMethods,
  useFormWatch,
} from '../../../../helpers/formManager';
import DateRangeField from '../base/DateRangeField';
import {
  fieldPathBuilders,
  fieldNames,
} from '../../shared/configs/acceptanceForm';
import { useValueRef } from '../../../../hooks/useValueRef';
import { endRules, startRules } from './constants';
import Btn, { btnTypes } from '../../../../components/btn';
import { useUniqueRangeIsImmutable } from '../../presenters/SetupDesks/hooks/isImmutableFns';
import { formatDate } from '../../shared/packages/dateTime';
import styles from './index.module.scss';

const { usePickerSettings } = DateRangeField;

const DeskDateRangeList = ({ deskIndex }) => {
  return (
    <FieldArrayProvider name={fieldPathBuilders.desk.dateRanges(deskIndex)}>
      <Container deskIndex={deskIndex}>
        <AppendRange />
      </Container>
    </FieldArrayProvider>
  );
};

export default DeskDateRangeList;

function Container({ deskIndex, children }) {
  const { remove, getFields } = useFieldArrayMethods();

  const uniqueRange = useFormWatch({
    name: fieldPathBuilders.desk.uniqueDateRange(deskIndex),
    exact: true,
  });

  useEffect(() => {
    if (!uniqueRange) {
      const fields = getFields();

      if (fields.length) {
        remove();
      }
    }
  }, [uniqueRange, getFields, remove]);

  return uniqueRange ? (
    <div className={styles.root}>
      <Content deskIndex={deskIndex} />
      {children}
    </div>
  ) : null;
}

const addedData = {
  [fieldNames.deskDateRange.fromDate]: null,
  [fieldNames.deskDateRange.toDate]: null,
};

function Content({ deskIndex }) {
  const fields = useFieldArrayState();
  const { append } = useFieldArrayMethods();

  useEffect(() => {
    if (!fields?.length) {
      append(addedData);
    }
  }, [fields, append]);

  return fields?.map((field, index) => (
    <Row key={field.id} index={index} deskIndex={deskIndex} />
  ));
}

const { fromDate: createFromDateName, toDate: createToDateName } =
  fieldPathBuilders.deskDateRange;

function Row({ deskIndex, index }) {
  const fromDateName = createFromDateName(deskIndex, index);
  const toDateName = createToDateName(deskIndex, index);

  const { getValues } = useFormMethods();

  const startData = {
    name: fromDateName,
    rules: startRules,
  };
  const endData = {
    name: toDateName,
    rules: endRules,
  };

  const rangeStr = useMemo(() => {
    return getValues([fromDateName, toDateName])
      .map((date) => formatDate(date))
      .join('-');
  }, [getValues, fromDateName, toDateName]);

  const disabled = useUniqueRangeIsImmutable(deskIndex, rangeStr);

  const pickerSettings = usePickerSettings(startData, endData);

  const canRemove = index > 0 && !disabled;

  return (
    <div className={styles.rowContainer}>
      <DateRangeField pickerSettings={pickerSettings} disabled={disabled} />
      <ValidateByAcceptancePeriod fromName={fromDateName} toName={toDateName} />
      {canRemove ? (
        <RemoveRange index={index} />
      ) : (
        <div className={styles.dummy} />
      )}
    </div>
  );
}

function AppendRange() {
  const { append } = useFieldArrayMethods();

  return (
    <Btn
      type={btnTypes.secondary}
      onClick={() => append(addedData)}
      style={{ fontWeight: 600 }}
    >
      Добавить еще диапазон приема помещений
    </Btn>
  );
}

function RemoveRange({ index }) {
  const { remove } = useFieldArrayMethods();

  return (
    <Tooltip title="Удалить диапазон" placement="topRight">
      <Button
        icon="delete"
        type="danger"
        onClick={() => remove(index)}
        className={styles.removeRowBtn}
        ghost
      />
    </Tooltip>
  );
}

function ValidateByAcceptancePeriod({ fromName, toName }) {
  const { trigger } = useFormMethods();

  const [fromDate, toDate, deskFromDate, deskToDate] = useFormWatch({
    name: [fieldNames.main.fromDate, fieldNames.main.toDate, fromName, toName],
  });

  const valuesRef = useValueRef({
    fromName,
    toName,
    deskFromDate,
    deskToDate,
  });

  useEffect(() => {
    if (valuesRef.current.deskFromDate) {
      trigger(valuesRef.current.fromName);
    }
  }, [fromDate, trigger, valuesRef]);

  useEffect(() => {
    if (valuesRef.current.deskToDate) {
      trigger(valuesRef.current.toName);
    }
  }, [toDate, trigger, valuesRef]);

  return null;
}
